import * as React from 'react';
import dayjs from "utils/dayjs";
import { LocalizationProvider, DateCalendar, DigitalClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, Popover, Box, Typography, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import useConfig from "hooks/useConfig";
import { useTranslation } from "react-i18next";

// 프로젝트 설정값 (환경변수 기반)
const APP_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'Asia/Seoul';

export const CustomMuiDateTimePicker = ({ field, value, name, setValue, fieldState, ...restProps }) => {
    const { t, i18n } = useTranslation();
    const { mode } = useConfig();
    const isDark = mode === 'dark';
    const [anchorEl, setAnchorEl] = React.useState(null);

    // 1. 다국어 로케일 및 날짜 포맷 추출
    const activeLocale = (i18n.language || process.env.REACT_APP_LANGUAGE || 'ko').toLowerCase();
    const currentDisplayFormat = t('message.common.text.date.format.datetime') || 'YYYY-MM-DD HH:mm';

    // 2. 서버 전송용 포맷 변환 함수 (타임존 적용 ISO 포맷)
    const toServerFormat = (date) => {
        if (!date) return null;
        return dayjs(date).tz(APP_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ');
    };

    // 3. 🎯 파란 원 100% 보장을 위한 safeValue 정제 (다국어 구분자 처리 + 지정 타임존)
    const safeValue = React.useMemo(() => {
        const rawVal = field?.value || value;
        if (!rawVal) return null;

        let d = dayjs(rawVal);
        if (!d.isValid()) {
            // 다국어 포맷으로 들어왔을 경우
            d = dayjs(rawVal, currentDisplayFormat);
        }

        if (!d.isValid()) return null;

        // MUI DateCalendar 파란 원 표시를 위해 순수 연/월/일/시/분 객체로 재구성
        return dayjs(d).tz(APP_TIMEZONE);
    }, [field?.value, value, currentDisplayFormat]);

    // 4. 화면 Input에 뿌려줄 표시용 텍스트
    const displayValue = React.useMemo(() => {
        const rawVal = field?.value || value;
        if (!rawVal) return "";
        if (safeValue && safeValue.isValid()) return safeValue.format(currentDisplayFormat);
        return rawVal;
    }, [field?.value, value, safeValue, currentDisplayFormat]);

    const openPopover = (event) => setAnchorEl(event.currentTarget);
    const closePopover = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    // 5. 부모 폼(React Hook Form / custom state)에 값 반영
    const commitChange = (newValue) => {
        if (!newValue) {
            if (setValue && name) setValue(name, null, { shouldDirty: true });
            if (field?.onChange) field.onChange(null);
            return;
        }

        const tzDate = dayjs(newValue).tz(APP_TIMEZONE);
        const formatted = toServerFormat(tzDate);

        if (setValue && name) setValue(name, formatted, { shouldDirty: true });
        if (field?.onChange) field.onChange(formatted);
    };

    // 6. 텍스트 직접 입력 기능 (한국용 소스의 장점 유지)
    const handleInputChange = (e) => {
        const inputVal = e.target.value;
        if (!/^[0-9.\-:\s]*$/.test(inputVal)) return;
        if (field?.onChange) field.onChange(inputVal);
    };

    const handleInputBlur = () => {
        const rawVal = field?.value;
        if (!rawVal || !String(rawVal).trim()) {
            commitChange(null);
            return;
        }

        const normalized = String(rawVal).trim().replace(/\./g, "-");
        const parsed = dayjs(normalized).tz(APP_TIMEZONE);

        if (normalized.length >= 16 && parsed.isValid()) {
            commitChange(parsed);
        }
    };

    const handleDateChange = (newDate) => {
        if (!newDate) return;
        const base = safeValue || dayjs().tz(APP_TIMEZONE);
        const updated = base.year(newDate.year()).month(newDate.month()).date(newDate.date());
        commitChange(updated);
    };

    const handleTimeChange = (newTime) => {
        if (!newTime) return;
        const base = safeValue || dayjs().tz(APP_TIMEZONE);
        const updated = base.hour(newTime.hour()).minute(newTime.minute()).second(0);
        commitChange(updated);
        closePopover();
    };

    // 7. useConfig 기반 동적 테마 색상 (다크모드/라이트모드 자동 대응)
    const colors = {
        bg: isDark ? '#0f172a' : '#ffffff',
        text: isDark ? '#e2e8f0' : '#000000',
        border: isDark ? '#334155' : '#cbd5e1',
        headerBg: isDark ? '#1e293b' : '#f8fafc'
    };

    return (
        <LocalizationProvider 
            dateAdapter={AdapterDayjs} 
            dateLibInstance={dayjs} 
            adapterLocale={activeLocale}
        >
            <TextField
                {...restProps}
                size="small"
                autoComplete="off"
                value={displayValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder={currentDisplayFormat}
                error={!!fieldState?.error}
                helperText={fieldState?.error?.message}
                InputProps={{ 
                    endAdornment: (
                        <InputAdornment position="end" sx={{ m: 0, gap: '4px', position: 'relative', zIndex: 1 }}>
                            {(field?.value || value) && (
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        commitChange(null);
                                    }}
                                    sx={{ p: '1px' }}
                                >
                                    <CloseIcon sx={{ fontSize: '1rem', color: isDark ? '#a0aec0' : '#666' }} />
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={openPopover} sx={{ p: '1px' }}>
                                <CalendarTodayIcon sx={{ fontSize: '1rem', color: isDark ? '#a0aec0' : '#666' }} />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={{ 
                    width: restProps.style?.width || "100%",
                    '& .MuiOutlinedInput-root': { 
                        bgcolor: colors.bg,
                        pr: '8px',
                        '& fieldset': { borderColor: colors.border },
                        '&:hover fieldset': { borderColor: isDark ? '#475569' : '#94a3b8' },
                        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                    },
                    '& .MuiOutlinedInput-input': {
                        fontSize: '0.85rem',
                        letterSpacing: '-0.3px',
                        textAlign: 'center',
                        color: colors.text
                    },
                    '& .MuiInputBase-root': {
                        height: '46px',
                    }
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: { 
                        display: 'flex', 
                        flexDirection: 'row', 
                        bgcolor: `${colors.bg} !important`,
                        border: `1px solid ${colors.border}`,
                        backgroundImage: 'none'
                    }
                }}
            >
                <Box sx={{ p: 1, bgcolor: colors.bg }}>
                    <DateCalendar 
                        key={safeValue ? safeValue.format("YYYY-MM-DD") : "empty"}
                        value={safeValue} 
                        onChange={handleDateChange}
                        sx={{
                            '& .MuiTypography-root': { color: colors.text },
                            '& .MuiPickersDay-root': { 
                                color: colors.text,
                                // 🎯 파란 원 하이라이트 강제 적용
                                '&.Mui-selected': { 
                                    bgcolor: '#3b82f6 !important', 
                                    color: '#ffffff !important',
                                    fontWeight: 'bold'
                                }
                            },
                            // 일요일 빨간색 스타일
                            '& .MuiDayCalendar-weekHeader .MuiTypography-root:first-of-type': { color: '#ef4444 !important' },
                            '& .MuiPickersDay-root[aria-colindex="1"]': { color: '#ef4444 !important' }
                        }}
                    />
                </Box>
                <Box sx={{ borderLeft: `1px solid ${colors.border}`, width: '130px', display: 'flex', flexDirection: 'column', bgcolor: colors.bg }}>
                    <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: colors.headerBg }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: colors.text }}>
                            {t('message.common.subtitle.select.time') || 'Time'}
                        </Typography>
                    </Box>
                    <DigitalClock
                        value={safeValue}
                        onChange={handleTimeChange}
                        minutesStep={30}
                        sx={{
                            '& .MuiMenuItem-root': { 
                                color: colors.text,
                                '&.Mui-selected': {
                                    bgcolor: '#3b82f6 !important',
                                    color: '#ffffff !important',
                                }
                            }
                        }}
                    />
                </Box>
            </Popover>
        </LocalizationProvider>
    );
};